import { call, put, select, take, takeEvery } from "redux-saga/effects";
import type { Action, PayloadAction } from "@reduxjs/toolkit";
import request from "../../../utils/request";
import { API_ENDPOINT } from "../../../utils/constants";
import { handleAxiosError } from "../../../utils/APIValidations";
import { navigateTo } from "../../../utils/navigation";
import { saveLogin, loginSaved, loginSavingError } from "../Login/slice";
import {
  saveSignup,
  signupSaved,
  signupSavingError,
  saveVerifySignupOtp,
  verifySignupOtpSaved,
  verifySignupOtpError,
  resendSignupOtp,
  resendSignupOtpSaved,
  resendSignupOtpError,
} from "./slice";
import { selectCredentials } from "./selector";
import type {
  SignupCredentials,
  SignupPayload,
  VerifySignupOtpPayload,
} from "./types";

function jsonOptions(payload: object): RequestInit {
  return {
    mode: "cors",
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  };
}

export function* postSignup({ payload }: PayloadAction<SignupPayload>) {
  const requestURL = `${API_ENDPOINT}signup`;

  try {
    yield call(request, requestURL, jsonOptions(payload));
    yield put(signupSaved());
    navigateTo("/verify", { state: { flow: "signup" } });
  } catch (e) {
    yield call(handleAxiosError, e);
    yield put(signupSavingError());
  }
}

export function* verifySignupOtp({
  payload,
}: PayloadAction<VerifySignupOtpPayload>) {
  const credentials: SignupCredentials | null = yield select(
    selectCredentials,
  );

  if (!credentials) {
    yield put(verifySignupOtpError());
    return;
  }

  try {
    yield call(
      request,
      `${API_ENDPOINT}signup/verify-otp`,
      jsonOptions({ email: credentials.email, otp: payload.otp }),
    );
  } catch (e) {
    yield call(handleAxiosError, e);
    yield put(verifySignupOtpError());
    return;
  }

  // OTP verified — log the user straight in with the credentials they
  // entered at signup, reusing the Login saga, then route on its outcome.
  yield put(
    saveLogin({
      email: credentials.email,
      password: credentials.password,
    }),
  );
  const result: Action = yield take([
    loginSaved.type,
    loginSavingError.type,
  ]);
  yield put(verifySignupOtpSaved());
  navigateTo(result.type === loginSaved.type ? "/dashboard" : "/login");
}

export function* resendSignupOtpSaga() {
  const credentials: SignupCredentials | null = yield select(
    selectCredentials,
  );

  if (!credentials) {
    yield put(resendSignupOtpError());
    return;
  }

  try {
    yield call(
      request,
      `${API_ENDPOINT}signup`,
      jsonOptions({
        full_name: credentials.full_name,
        email: credentials.email,
        password: credentials.password,
        confirm_password: credentials.confirm_password,
      }),
    );
    yield put(resendSignupOtpSaved());
  } catch (e) {
    yield call(handleAxiosError, e);
    yield put(resendSignupOtpError());
  }
}

export default function* saga() {
  yield takeEvery(saveSignup.type, postSignup);
  yield takeEvery(saveVerifySignupOtp.type, verifySignupOtp);
  yield takeEvery(resendSignupOtp.type, resendSignupOtpSaga);
}
